import React, { useCallback } from "react";
import styled from "styled-components";
import { navigateToUrl } from "single-spa";

import {
  Text,
  Button as AsurionButton,
  breakpoints,
  colors,
  Size,
} from "@trailhead/asurion-ui";
import { FlexColumnContainer, useWindowDimensions } from "@trailhead/core-ui";
import { useSanitizedTranslation } from "@trailhead/i18n";
import { useQueryParams } from "@trailhead/smart-setup-core";
import { useMxContext } from "@trailhead/mx-context";
import { PartnerType } from "@trailhead/contentful";
import { trackEvent } from "@trailhead/analytics";

const GenericCTAV2Container = styled.div`
  display: flex;
  flex-direction: column-reverse;
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${colors.neutralDeep};
  background: ${colors.neutralBrightest};
  overflow: hidden;

  ${breakpoints.md} {
    height: 290px;
    flex-direction: row;
  }

  ${breakpoints.md2} {
    gap: 89px;
  }

  ${breakpoints.lg} {
    gap: 150px;
  }
`;

const ContentContainer = styled(FlexColumnContainer)`
  padding: 20px 16px;

  ${breakpoints.md} {
    padding: 10px 0px 10px 10px;
  }
`;

const TitleButtonContainer = styled(FlexColumnContainer)`
  gap: 16px;

  ${breakpoints.md} {
    padding: 24px 0px 24px 24px;
    width: 394px;
  }
`;

const TitleContainer = styled(FlexColumnContainer)`
  ${breakpoints.md} {
    width: 291px;
  }
`;
const TitleText = styled(Text)``;
const NoWrapTitleText = styled(Text)`
  white-space: nowrap;
`;

const SubtitleContainer = styled(FlexColumnContainer)``;
const SubtitleText = styled(Text)``;

const ButtonContainer = styled(FlexColumnContainer)`
  width: 219px;
`;
const AsurionButtonWrapper = styled(AsurionButton)``;

const ImageContainer = styled(FlexColumnContainer)`
  width: 507px;
  height: 290px;
  margin-left: auto;

  ${breakpoints.md} {
    margin-left: unset;
  }
`;
const ImageWrapper = styled.img``;

interface GenericCTAV2Props {
  title: string;
  noWrapTitle: string;
  subTitle: string;
  buttonText: string;
  imageSrc: string;
  buttonCallback?: () => void;
  buttonUrl?: string;
}

export const GenericCTAV2: React.FC<GenericCTAV2Props> = ({
  title,
  noWrapTitle,
  subTitle,
  buttonText,
  imageSrc,
  buttonCallback,
  buttonUrl,
}) => {
  const t = useSanitizedTranslation();

  const { width } = useWindowDimensions();
  const isLargeMobile = width >= Size.SM;
  const isTablet = width >= Size.MD;

  const queryParams = useQueryParams();
  const { client } = useMxContext();

  const defaultCallback = useCallback(() => {
    if (buttonUrl) {
      if (client === PartnerType.ATT) {
        trackEvent("AttProtectAdvantageButtonClick");
      } else if (client === PartnerType.VERIZON) {
        trackEvent("VerizonMobileProtectButtonClick");
      }
      window.location.href = buttonUrl;
    } else {
      trackEvent("SelfGuideNavigationButtonClick");
      queryParams.set("skipPathfinder", "true");
      navigateToUrl(`/mobile-device-setup/intro?${queryParams.toString()}`);
    }
  }, [buttonUrl, client, queryParams]);

  return (
    <GenericCTAV2Container>
      <ContentContainer>
        <TitleButtonContainer>
          <TitleContainer>
            <TitleText size={isLargeMobile ? 5 : 4} weight="feather">
              {t(title)}
              <NoWrapTitleText size={isLargeMobile ? 5 : 4} weight="feather">
                {t(noWrapTitle)}
              </NoWrapTitleText>
            </TitleText>
          </TitleContainer>
          <SubtitleContainer>
            <SubtitleText
              size={isTablet ? 3 : 2}
              weight="feather"
              color={colors.neutralDeepest}
            >
              {t(subTitle)}
            </SubtitleText>
          </SubtitleContainer>
          <ButtonContainer>
            <AsurionButtonWrapper
              iconSrc="ArrowRight"
              iconSide="right"
              size="medium"
              onClick={buttonCallback ?? defaultCallback}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: t(buttonText),
                }}
              />
            </AsurionButtonWrapper>
          </ButtonContainer>
        </TitleButtonContainer>
      </ContentContainer>
      <ImageContainer>
        <ImageWrapper src={imageSrc} />
      </ImageContainer>
    </GenericCTAV2Container>
  );
};
